// 007 — Interconsulta Solicitud (anverso) e Informe (reverso) (Anexo 1 MSP, SNS-MSP/HCU-form.007/2021). Fase 7, Tanda
// 12: bloques B-F de la solicitud y B-F del informe. Campos y decisiones en `bloques007Datos.ts`. Las dos mitades las
// llenan personas distintas en momentos distintos: solo se guarda la que tiene contenido (el informe exige elegir la
// solicitud de origen). El bloque A lo resuelve `EncabezadoMspCampos`; el profesional (G) lo arma el servidor.
import type { ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { SelectorRecurso } from './SelectorRecurso';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_CARACTERISTICA_007,
  CAMPOS_CRITERIO_007,
  CAMPOS_CUADRO_INFORME_007,
  CAMPOS_CUADRO_SOLICITUD_007,
  CAMPOS_PLAN_DIAGNOSTICO_007,
  CAMPOS_PLAN_REALIZADO_007,
  CAMPOS_PLAN_TERAPEUTICO_007,
  CAMPOS_RESULTADOS_007,
  estadoInicialInforme007,
  estadoInicialSolicitud007,
  faltantesObligatorios007,
  hayContenidoSolicitud007,
  paresInforme007,
  paresSolicitud007,
  prioridadFhir007,
  textoInforme007,
  textoMotivo007,
  type EstadoInforme007,
  type EstadoSolicitud007,
} from './bloques007Datos';

const CATEGORIA_INTERCONSULTA = { coding: [{ system: 'http://snomed.info/sct', code: '11429006', display: 'Consultation' }] };
const soloSimples = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const area = (campos: readonly CampoSimple[]): CampoSimple => campos.find((c) => c.tipo === 'area')!;

export function Formulario007({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [solicitud, setSolicitud] = useState<EstadoSolicitud007>(() => estadoInicialSolicitud007());
  const [origen, setOrigen] = useState<ServiceRequest>();
  const [informe, setInforme] = useState<EstadoInforme007>(() => estadoInicialInforme007());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  /** Solicitud ya guardada en un intento previo (la transacción se confirmó): otro guardado no la duplica. */
  const solicitudCreada = useRef(false);

  const paresInf = paresInforme007(informe);
  const guardaSolicitud = hayContenidoSolicitud007(solicitud);
  const guardaInforme = origen?.id !== undefined && paresInf.length > 0;
  const titulo = guardaSolicitud && guardaInforme ? t('formularios.f007.tituloAmbas') : guardaInforme ? t('formularios.f007.tituloInforme') : t('formularios.f007.tituloSolicitud');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '007', titulo, { servicio: solicitud.caracteristica.servicio });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();
      const pares: Par[] = [];
      const lote = new LoteTransaccional();
      let solicitudEnLote = false;

      const faltan = faltantesObligatorios007(solicitud, informe);
      if (faltan.length > 0) throw new Error(t('formularios.f007.faltaObligatorio', { campos: faltan.join(', ') }));
      if (paresInf.length > 0 && !origen?.id) throw new Error(t('formularios.f007.faltaOrigen'));
      if (guardaSolicitud && !solicitudCreada.current) {
        const especialidadConsultada = (solicitud.caracteristica.especialidadConsultada ?? '').trim();
        const reasonCode = solicitud.diagnosticos.filter((d) => d.diagnostico.texto.trim()).map((d) => codeDeDiagnostico(d.diagnostico));
        const motivo = textoMotivo007(solicitud);
        lote.crear<ServiceRequest>({
          resourceType: 'ServiceRequest',
          status: 'active',
          intent: 'order',
          priority: prioridadFhir007(solicitud.caracteristica.urgente),
          category: [CATEGORIA_INTERCONSULTA],
          code: { text: `Interconsulta${especialidadConsultada ? ` a ${especialidadConsultada}` : ''}` },
          subject,
          encounter,
          authoredOn: ahora,
          performer: especialidadConsultada ? [{ display: especialidadConsultada }] : undefined,
          reasonCode: [...(motivo ? [{ text: motivo }] : []), ...reasonCode],
        });
        solicitudEnLote = true;
        pares.push(...paresSolicitud007(solicitud));
      }

      if (guardaSolicitud && solicitudCreada.current && pares.length === 0) pares.push(...paresSolicitud007(solicitud));
      if (guardaInforme && origen?.id) {
        lote.crear({
          resourceType: 'Communication',
          status: 'completed',
          subject,
          encounter,
          basedOn: [{ reference: `ServiceRequest/${origen.id}` }],
          sent: ahora,
          payload: [{ contentString: textoInforme007(informe) }],
        });
        pares.push(...paresInf.map((p) => ({ pregunta: `Informe — ${p.pregunta}`, respuesta: p.respuesta })));
      }

      await lote.enviar(medplum);
      if (solicitudEnLote) solicitudCreada.current = true;
      onGuardado(pares, datosEncabezadoMsp);
    });

  const areaDe = (lista: readonly CampoSimple[], id: string, valor: string, filas: number, poner: (v: string) => void): JSX.Element => (
    <AreaDictado id={id} etiqueta={area(lista).etiqueta} valor={valor} filas={filas} onChange={poner} />
  );

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f007.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="text-sm font-semibold text-hce-ink">{t('formularios.f007.solicitudTitulo')}</h4>

      <Bloque titulo={t('formularios.f007.caracteristicaTitulo')}>
        <CamposSimples prefijoId="f007-car" campos={soloSimples(CAMPOS_CARACTERISTICA_007)} valores={solicitud.caracteristica} columnas={3} onChange={(v) => setSolicitud({ ...solicitud, caracteristica: { ...solicitud.caracteristica, ...v } })} />
        {areaDe(CAMPOS_CARACTERISTICA_007, 'f007-motivo', solicitud.caracteristica.motivo ?? '', 3, (v) => setSolicitud({ ...solicitud, caracteristica: { ...solicitud.caracteristica, motivo: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.cuadroTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f007.cuadroNota')}</p>
        {areaDe(CAMPOS_CUADRO_SOLICITUD_007, 'f007-cuadro', solicitud.cuadro.cuadro ?? '', 5, (v) => setSolicitud({ ...solicitud, cuadro: { cuadro: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.resultadosTitulo')}>
        {areaDe(CAMPOS_RESULTADOS_007, 'f007-resultados', solicitud.resultados.resultados ?? '', 4, (v) => setSolicitud({ ...solicitud, resultados: { resultados: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f007.diagnosticoNota')}</p>
        {solicitud.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f007.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f007.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setSolicitud({ ...solicitud, diagnosticos: solicitud.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.planRealizadoTitulo')}>
        {areaDe(CAMPOS_PLAN_REALIZADO_007, 'f007-plan', solicitud.plan.plan ?? '', 4, (v) => setSolicitud({ ...solicitud, plan: { plan: v } }))}
      </Bloque>

      <h4 className="text-sm font-semibold text-hce-ink">{t('formularios.f007.informeTitulo')}</h4>

      <SelectorRecurso<'ServiceRequest'>
        resourceType="ServiceRequest"
        subject={subject}
        label={t('formularios.f007.solicitudOrigen')}
        etiqueta={(r) => r.code?.text ?? r.reasonCode?.find((c) => c.text)?.text ?? r.id ?? ''}
        value={origen}
        onChange={setOrigen}
        filtro={{ category: 'http://snomed.info/sct|11429006' }}
      />

      <Bloque titulo={t('formularios.f007.informeCuadroTitulo')}>
        {areaDe(CAMPOS_CUADRO_INFORME_007, 'f007-i-cuadro', informe.cuadro.cuadro ?? '', 4, (v) => setInforme({ ...informe, cuadro: { cuadro: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.criterioTitulo')}>
        {areaDe(CAMPOS_CRITERIO_007, 'f007-i-criterio', informe.criterio.criterio ?? '', 4, (v) => setInforme({ ...informe, criterio: { criterio: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.informeDiagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f007.diagnosticoNota')}</p>
        {informe.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f007.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f007.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setInforme({ ...informe, diagnosticos: informe.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.planDiagnosticoTitulo')}>
        {areaDe(CAMPOS_PLAN_DIAGNOSTICO_007, 'f007-i-pdiag', informe.planDiagnostico.planDiagnostico ?? '', 4, (v) => setInforme({ ...informe, planDiagnostico: { planDiagnostico: v } }))}
      </Bloque>
      <Bloque titulo={t('formularios.f007.planTerapeuticoTitulo')}>
        {areaDe(CAMPOS_PLAN_TERAPEUTICO_007, 'f007-i-pter', informe.planTerapeutico.planTerapeutico ?? '', 4, (v) => setInforme({ ...informe, planTerapeutico: { planTerapeutico: v } }))}
      </Bloque>

      <Bloque titulo={t('formularios.f007.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || (!guardaSolicitud && paresInf.length === 0)}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
